import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, Clock, FileText, Loader2, RefreshCw, ShieldAlert, ShieldCheck, Sparkles, X } from "lucide-react";
import { cva } from "class-variance-authority";
import { api, errMsg } from "../lib/api.js";
import { useToast } from "../context/ToastContext.js";
import { AsyncButton } from "./ui/async-button.js";
import { cn } from "../lib/utils.js";
import { SmartBitRejection, type SmartBitVerdict } from "../lib/resumeUpload.js";
import { Badge } from "./ui/badge.js";
import { Button } from "./ui/button.js";
import { PanelReveal } from "./ui/panel-reveal.js";
import { useVirtualHr } from "../lib/virtualHr.js";

// Integer Smart Bit: shows what the AI check is doing and, when a file is refused,
// exactly why. It reports the real upload/verify flow; it never simulates progress.

type Phase = "checking" | "accepted" | "rejected" | "error";

/** What HR checks before pressing Verify, and what happens once they do. */
export interface SmartBitReview {
  versionId: string;
  fields: { label: string; value: string; group?: string }[];
  /** Runs after the verify call succeeds: fills the profile fields, refreshes the list. */
  onVerified: () => void;
  verified: boolean;
}

export interface SmartBitState {
  review: SmartBitReview | null;
  phase: Phase;
  label: string;
  fileName: string;
  previewUrl: string | null;
  isImage: boolean;
  verdict: SmartBitVerdict | null;
  message: string;
  startedAt: number;
  retry: (f: File) => void;
}

export interface UploadHelpers {
  onVerdict: (v: SmartBitVerdict) => void;
  onReview: (r: Omit<SmartBitReview, "verified">) => void;
}

interface RunInput {
  label: string;
  file: File;
  retry: (f: File) => void;
}

const AUTO_CLOSE_MS = 5000;

export function useSmartBitPanel() {
  const [state, setState] = useState<SmartBitState | null>(null);
  const [open, setOpen] = useState(false);
  const touched = useRef(false);
  const urlRef = useRef<string | null>(null);

  const revoke = () => { if (urlRef.current) { URL.revokeObjectURL(urlRef.current); urlRef.current = null; } };
  useEffect(() => revoke, []);

  const run = useCallback(async ({ label, file, retry }: RunInput, upload: (h: UploadHelpers) => Promise<unknown>) => {
    revoke();
    const isImage = file.type.startsWith("image/");
    const previewUrl = isImage ? URL.createObjectURL(file) : null;
    urlRef.current = previewUrl;
    touched.current = false;
    const base = { label, fileName: file.name, previewUrl, isImage, startedAt: Date.now(), retry };
    let review: SmartBitReview | null = null;
    setState({ ...base, review: null, phase: "checking", verdict: null, message: "" });
    setOpen(true);
    let verdict: SmartBitVerdict | null = null;
    try {
      await upload({ onVerdict: (v) => { verdict = v; }, onReview: (r) => { review = { ...r, verified: false }; } });
      setState({ ...base, review, phase: "accepted", verdict, message: (verdict as SmartBitVerdict | null)?.message ?? `Verified as ${label}` });
    } catch (e) {
      if (e instanceof SmartBitRejection) setState({ ...base, review: null, phase: "rejected", verdict: e.verdict, message: e.message });
      else setState({ ...base, review: null, phase: "error", verdict, message: errMsg(e) ?? "Upload failed" });
    }
  }, []);

  // A clean pass closes itself shortly after, unless the user has touched the panel.
  const phase = state?.phase;
  const awaitingHr = !!state?.review && !state.review.verified;
  useEffect(() => {
    if (phase !== "accepted" || awaitingHr) return;
    const t = window.setTimeout(() => { if (!touched.current) setOpen(false); }, AUTO_CLOSE_MS);
    return () => window.clearTimeout(t);
  }, [phase, awaitingHr, state?.startedAt]);

  const toast = useToast();
  // Reuses the existing HR verify endpoint (same call as the document viewer's Verify).
  const verify = async () => {
    const r = state?.review;
    if (!r) return;
    try {
      await api.patch(`/documents/versions/${r.versionId}/verify`, {});
    } catch (e) {
      toast.show(errMsg(e) ?? "Could not verify this document. Please try again.", "error");
      throw e;
    }
    r.onVerified();
    setState((prev) => (prev?.review ? { ...prev, review: { ...prev.review, verified: true } } : prev));
  };

  return {
    state, open, run, verify,
    touch: () => { touched.current = true; },
    close: () => setOpen(false),
    reopen: () => setOpen(true),
  };
}

type Tone = "run" | "ok" | "warn" | "bad" | "pend" | "idle";

const toneBox = cva("rounded-lg border", {
  variants: {
    tone: {
      run: "border-[var(--color-info-accent)]/40 bg-[var(--color-info-bg)] text-[var(--color-info-fg)]",
      ok: "border-[var(--color-success)]/40 bg-[var(--color-success-bg)] text-[var(--color-success-fg)]",
      warn: "border-[var(--color-warning)]/40 bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)]",
      bad: "border-[var(--color-error)]/40 bg-[var(--color-error-bg)] text-[var(--color-error-fg)]",
      pend: "border-[var(--color-accent-fg)]/40 bg-[var(--color-accent-bg)] text-[var(--color-accent-fg)]",
      idle: "border-surface-200 bg-surface-50 text-surface-500",
    },
  },
  defaultVariants: { tone: "idle" },
});

const dot = cva("inline-flex size-7 shrink-0 items-center justify-center rounded-full border-[1.5px] text-xs font-bold", {
  variants: {
    tone: {
      run: "border-[var(--color-info-accent)] text-[var(--color-info-fg)]",
      ok: "border-transparent bg-success text-white",
      warn: "border-[var(--color-warning)] text-[var(--color-warning-fg)]",
      bad: "border-transparent bg-error text-white",
      pend: "border-[var(--color-accent-fg)] text-[var(--color-accent-fg)]",
      idle: "border-surface-300 text-surface-500",
    },
  },
});

const textTone: Record<Tone, string> = {
  run: "text-[var(--color-info-fg)]", ok: "text-[var(--color-success-fg)]", warn: "text-[var(--color-warning-fg)]",
  bad: "text-[var(--color-error-fg)]", pend: "text-[var(--color-accent-fg)]", idle: "text-surface-500",
};

function useElapsed(active: boolean, since: number) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    if (!active) return;
    const tick = () => setSecs(Math.floor((Date.now() - since) / 1000));
    tick();
    const t = window.setInterval(tick, 1000);
    return () => window.clearInterval(t);
  }, [active, since]);
  return secs;
}

const REJECT_TITLE: Record<NonNullable<SmartBitVerdict["code"]>, string> = {
  WRONG_DOCUMENT: "This is not the right document",
  SUSPECTED_FAKE: "This does not look like a genuine document",
  UNREADABLE: "The image is hard to read",
  UNSUPPORTED_FILE: "This file format is not supported",
  IDENTITY_MISMATCH: "Please upload your own document",
};

// Review rows arrive in order with an optional group name; consecutive rows of a group share a heading.
function groupFields(fields: { label: string; value: string; group?: string }[]) {
  const out: { group: string; rows: { label: string; value: string }[] }[] = [];
  for (const f of fields) {
    const g = f.group ?? "";
    const last = out[out.length - 1];
    if (last && last.group === g) last.rows.push(f);
    else out.push({ group: g, rows: [f] });
  }
  return out;
}

function Step({ tone, icon, title, sub }: { tone: Tone; icon: React.ReactNode; title: string; sub: string }) {
  return (
    <li className="flex items-center gap-2.5" aria-current={tone === "run" ? "step" : undefined}>
      <span className={dot({ tone })}>{icon}</span>
      <div className="min-w-0">
        <b className="block text-[13px] text-surface-800">{title}</b>
        <span className={cn("block max-w-[210px] text-[11.5px]", textTone[tone])}>{sub}</span>
      </div>
    </li>
  );
}

function Chips({ title, tone, items }: { title: string; tone: Tone; items: string[] }) {
  if (!items.length) return null;
  return (
    <div className="mt-2.5">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-surface-500">{title}</p>
      <ul className="flex flex-wrap gap-1.5">
        {items.map((it) => (
          <li key={it} className={cn("rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold", toneBox({ tone }))}>{it}</li>
        ))}
      </ul>
    </div>
  );
}

function Panel({ s, onHide, onTouch, onVerify }: { s: SmartBitState; onHide: () => void; onTouch: () => void; onVerify: () => Promise<void> }) {
  const fileInput = useRef<HTMLInputElement>(null);
  const { assistantName: name, shortName: short } = useVirtualHr();
  const secs = useElapsed(s.phase === "checking", s.startedAt);
  const v = s.verdict;
  const manual = !!v?.unavailable;

  const tone: Tone = s.phase === "checking" ? "run" : s.phase === "accepted" ? (manual ? "warn" : "ok") : "bad";
  const step1: { tone: Tone; icon: React.ReactNode; sub: string } =
    s.phase === "checking" ? { tone: "run", icon: <Loader2 size={14} className="animate-spin" />, sub: `Verifying… ${secs}s` }
    : s.phase === "accepted" ? (manual
        ? { tone: "warn", icon: <AlertTriangle size={14} />, sub: "Check unavailable, manual review" }
        : { tone: "ok", icon: <Check size={14} />, sub: `Passed${v ? ` · ${Math.round(v.confidence * 100)}%` : ""}` })
    : { tone: "bad", icon: <X size={14} />, sub: s.phase === "error" ? "Could not complete" : "Action needed" };
  const step2: { tone: Tone; icon: React.ReactNode; sub: string } =
    s.phase === "accepted" && s.review?.verified
      ? { tone: "ok", icon: <Check size={14} />, sub: "Verified by HR" }
    : s.phase === "accepted"
      ? { tone: "pend", icon: <Clock size={14} />, sub: s.review ? "Waiting for your review" : "Pending · HR will review" }
      : { tone: "idle", icon: "2", sub: `Starts after ${short}'s check` };

  const title = v?.code ? REJECT_TITLE[v.code] : s.phase === "error" ? "Upload failed" : "";
  const footer =
    s.phase === "checking" ? `${name} is checking this document. You can keep working; the result appears here.`
    : s.phase === "accepted" && s.review?.verified ? "Verified. The extracted details were filled into the profile fields."
    : s.phase === "accepted" && s.review ? "Check passed. Review the extracted details above, then press Verify to approve and fill them into the profile."
    : s.phase === "accepted" ? (manual ? "The automatic check was unavailable. The document is saved and HR will review it manually."
        : "Check passed. The status stays Pending until HR approves it. You can keep working and you will be notified.")
    : "The check did not pass, so this file was not uploaded and HR has not seen it. Choose the correct file to try again.";

  return (
    <div
      className="rounded-2xl border border-surface-200 bg-surface-50 bg-[radial-gradient(70%_90%_at_12%_0%,rgba(75,123,229,0.10),transparent_60%)] p-4"
      role="region" aria-live="polite" aria-label={`${s.label} verification`}
      onPointerDown={onTouch} onFocusCapture={onTouch}
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="min-w-[140px]">
          <p className="text-[15px] font-bold text-surface-900">{s.label}</p>
          <p className="text-[11.5px] text-surface-500">{name}</p>
        </div>
        <ol className="mx-auto flex items-center gap-4">
          <Step title={`${short} check`} {...step1} />
          <li aria-hidden className="relative h-0.5 w-16 overflow-hidden rounded bg-surface-200">
            <i className={cn("absolute inset-0 origin-left bg-gradient-to-r from-success to-[var(--color-accent-fg)] transition-transform duration-500", s.phase === "accepted" ? "scale-x-100" : "scale-x-0")} />
          </li>
          <Step title="HR approval" {...step2} />
        </ol>
        <Button size="sm" variant="secondary" className="ml-auto" onClick={onHide}>Hide</Button>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-[minmax(0,260px)_minmax(0,1fr)]">
        <div className="relative h-[200px] overflow-hidden rounded-xl border border-surface-200 bg-surface-100">
          {s.isImage && s.previewUrl
            ? <img src={s.previewUrl} alt={`Preview of ${s.fileName}`} className="absolute inset-0 size-full object-contain" />
            : <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-surface-500"><FileText size={32} /><span className="px-3 text-center text-xs">{s.fileName}</span></div>}
          {s.phase === "checking" && <div className="sb-scan-line" aria-hidden />}
          <span className={cn("absolute bottom-2 left-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-bold backdrop-blur", toneBox({ tone }))}>
            {s.phase === "checking" ? <><Sparkles size={11} /> {short} is scanning…</>
              : s.phase === "accepted" ? "Pending HR approval"
              : v?.detectedType && v.code !== "IDENTITY_MISMATCH" ? `Detected: ${v.detectedType}` : "Not accepted"}
          </span>
        </div>

        <div className="min-w-0">
          {s.phase === "checking" && (
            <div className={cn("p-3 text-[12.5px]", toneBox({ tone: "run" }))}>
              <p className="flex items-center gap-2 font-semibold"><Loader2 size={14} className="animate-spin" /> Reading {s.fileName}</p>
              <p className="mt-1 text-surface-600">Checking that this is a genuine {s.label}. This usually takes a few seconds.</p>
            </div>
          )}

          {s.phase === "accepted" && v && (
            <div className={cn("p-3", toneBox({ tone: manual ? "warn" : "ok" }))}>
              <p className="flex items-center gap-2 text-[13.5px] font-bold"><ShieldCheck size={16} /> {manual ? "Saved for manual review" : s.message}</p>
              {!manual && (
                <p className="mt-1 text-[12.5px] text-surface-600">
                  Detected {v.detectedType} · {Math.round(v.confidence * 100)}% sure{v.clarity != null ? ` · clarity ${v.clarity}%` : ""}
                </p>
              )}
              <Chips title="Picture quality" tone="warn" items={v.qualityIssues} />
              {v.crossCheck && (
                <Chips
                  title={`Identity check vs ${v.crossCheck.against === "PAN" ? "PAN card" : "Aadhaar card"}`}
                  tone={v.crossCheck.mismatch ? "warn" : "ok"}
                  items={v.crossCheck.mismatch ? v.crossCheck.notes : ["Name and date of birth match"]}
                />
              )}
              {s.review && (
                <div className="mt-3 rounded-lg border border-surface-200 bg-surface-0 p-3 text-surface-800">
                  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-surface-500">Extracted from the document</p>
                  {s.review.fields.length > 0 ? (
                    <div className="max-h-80 space-y-3 overflow-y-auto pr-1">
                      {groupFields(s.review.fields).map((g) => (
                        <section key={g.group}>
                          {g.group && <h5 className="mb-1 border-b border-surface-200 pb-0.5 text-[11px] font-bold uppercase tracking-wide text-[var(--color-info-fg)]">{g.group}</h5>}
                          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-[13px]">
                            {g.rows.map((f) => (
                              <div key={f.label} className="contents">
                                <dt className="whitespace-nowrap text-surface-500">{f.label}</dt>
                                <dd className="whitespace-pre-line break-words font-semibold">{f.value}</dd>
                              </div>
                            ))}
                          </dl>
                        </section>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[12.5px] text-surface-500">No fields are read from this document type. Check the document, then verify.</p>
                  )}
                  <div className="mt-3">
                    {s.review.verified
                      ? <Badge tone="success"><Check size={12} /> Verified</Badge>
                      : <AsyncButton size="sm" variant="success" onClick={() => onVerify()}>Verify</AsyncButton>}
                  </div>
                </div>
              )}
            </div>
          )}

          {(s.phase === "rejected" || s.phase === "error") && (
            <div className={cn("p-3", toneBox({ tone: "bad" }))}>
              <h4 className="flex items-center gap-2 text-[13.5px] font-bold"><ShieldAlert size={16} /> {title}</h4>
              <p className="mt-1 text-[12.5px] leading-[18px] text-surface-700">{s.message}</p>
              {v && v.code !== "UNSUPPORTED_FILE" && v.code !== "IDENTITY_MISMATCH" && (
                <p className="mt-1.5 text-[12px] text-surface-600">
                  Detected: <b>{v.detectedType}</b> · {Math.round(v.confidence * 100)}% sure{v.clarity != null ? ` · clarity ${v.clarity}%` : ""}
                </p>
              )}
              {v && v.code !== "IDENTITY_MISMATCH" && (
                <>
                  <Chips title="Why it was refused" tone="bad" items={v.reasons} />
                  <Chips title="Picture quality" tone="warn" items={v.qualityIssues} />
                  <Chips title="What looked wrong" tone="bad" items={v.suspicions} />
                </>
              )}
              <div className="mt-3">
                <input ref={fileInput} type="file" accept="image/*,.pdf,.doc,.docx" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) s.retry(f); e.target.value = ""; }} />
                <Button size="sm" variant="secondary" onClick={() => fileInput.current?.click()}>
                  <RefreshCw size={12} /> Choose another file
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={cn("mt-4 flex items-center gap-3 px-3.5 py-2.5 text-[13px] leading-[18px]", toneBox({ tone: s.phase === "checking" ? "run" : s.phase === "accepted" ? "pend" : "warn" }))}>
        {s.phase === "checking" ? <Loader2 size={16} className="shrink-0 animate-spin" /> : s.phase === "accepted" ? <Clock size={16} className="shrink-0" /> : <AlertTriangle size={16} className="shrink-0" />}
        <span className="text-surface-700">{footer}</span>
      </div>
    </div>
  );
}

// Sits under the six slots. Renders nothing until a file is picked, so the idle screen is unchanged.
export function SmartBitPanel({ panel }: { panel: ReturnType<typeof useSmartBitPanel> }) {
  if (!panel.state) return null;
  return (
    <PanelReveal open={panel.open} className="col-span-full">
      <Panel s={panel.state} onHide={panel.close} onTouch={panel.touch} onVerify={panel.verify} />
    </PanelReveal>
  );
}

// Section-header pill. Visible only while there is something to report.
export function SmartBitHeaderChip({ panel }: { panel: ReturnType<typeof useSmartBitPanel> }) {
  const { shortName: short } = useVirtualHr();
  const s = panel.state;
  if (!s) return null;
  const cfg: { tone: Tone; text: string } =
    s.phase === "checking" ? { tone: "run", text: `${short} is verifying 1 document` }
    : s.phase === "rejected" || s.phase === "error" ? { tone: "warn", text: "1 needs your action" }
    : { tone: "pend", text: "1 pending HR approval" };
  return (
    <button type="button" onClick={panel.reopen} title="Show verification details" className="cursor-pointer">
      <Badge tone={cfg.tone === "run" ? "info" : cfg.tone === "warn" ? "warning" : "accent"} className="gap-2 py-1 text-xs">
        <i className="sb-pulse-dot size-1.5 rounded-full bg-current" />
        {cfg.text}
      </Badge>
    </button>
  );
}
